import { useCallback, useMemo, useState } from 'react';
import { useWizard } from '@/_shared/hooks/useWizard';
import { MacroStep } from './types';

type UseWizardNavigationProps = {
  stepsConfig: MacroStep[];
};
/**
 * A custom React hook for managing navigation within a wizard-like interface.
 * It provides functionality to navigate through macro and micro steps, retrieve active step data, and handle step-specific actions.
 *
 * @param {UseWizardNavigationProps} props - The properties for the hook.
 * @param {MacroStep[]} props.stepsConfig - An array of macro steps, where each macro step contains its own micro steps.
 *
 * @returns {Object} - The hook returns an object containing:
 * - `activeStep` (number): The current active step index (global index across all macro and micro steps).
 * - `activeMacroStep` (number): The index of the currently active macro step.
 * - `activeMicroStep` (number): The index of the currently active micro step within the active macro step.
 * - `activeMacroStepData` (MacroStep): The data of the currently active macro step.
 * - `activeMicroStepData` (Object): The data of the currently active micro step.
 * - `totalSteps` (number): The total number of steps across all macro steps.
 * - `isLoadingNextButton` (boolean): Indicates whether the "Next" button is in a loading state.
 * - `disableNextButton` (boolean): Indicates whether the "Next" button is disabled.
 * - `disablePreviousButton` (boolean): Indicates whether the "Previous" button is disabled.
 * - `disableSkipButton` (boolean): Indicates whether the "Skip" button is disabled.
 * - `onNext` (function): A function to move to the next step.
 * - `onPrevious` (function): A function to move to the previous step.
 * - `goTo` (function): A function to navigate to a specific step by its global index.
 * - `handleNext` (function): A function to handle the "next" action, including any custom logic defined in the current micro step.
 * - `handlePrevious` (function): A function to handle the "previous" action, including any custom logic defined in the current micro step.
 * - `handleSkip` (function): A function to handle the "skip" action, including any custom logic defined in the current micro step.
 *
 * Dependencies:
 * - React's `useState`, `useMemo`, and `useCallback` hooks.
 * - A `MacroStep` type that defines the structure of the steps configuration.
 * - `useWizard` hook for managing wizard-related state and actions.
 *
 * @example
 * Example usage:
 * ```javascript
 * import { useWizardNavigation } from './useWizardNavigation';
 *
 * const stepsConfig = [
 *   { steps: [{ onNext: () => console.log('Step 1 next') }, { onNext: () => console.log('Step 2 next') }] },
 *   { steps: [{ onNext: () => console.log('Step 3 next') }] },
 * ];
 *
 * function Wizard() {
 *   const {
 *     activeStep,
 *     activeMacroStep,
 *     activeMicroStep,
 *     activeMacroStepData,
 *     activeMicroStepData,
 *     totalSteps,
 *     isLoadingNextButton,
 *     disableNextButton,
 *     disablePreviousButton,
 *     disableSkipButton,
 *     onNext,
 *     onPrevious,
 *     goTo,
 *     handleNext,
 *     handlePrevious,
 *     handleSkip,
 *   } = useWizardNavigation({ stepsConfig });
 *
 *   return (
 *     <div>
 *       <h1>Wizard Navigation</h1>
 *       <p>Active Step: {activeStep}</p>
 *       <p>Active Macro Step: {activeMacroStep}</p>
 *       <p>Active Micro Step: {activeMicroStep}</p>
 *       <button onClick={handlePrevious}>Previous</button>
 *       <button onClick={handleNext}>Next</button>
 *       <button onClick={handleSkip}>Skip</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useWizardNavigation({ stepsConfig }: UseWizardNavigationProps) {
  const {
    handleNext: wizardHandleNext,
    handlePrevious: wizardHandlePrevious,
    handleSkip: wizardHandleSkip,
    isLoadingNextButton,
    disableNextButton,
    disablePreviousButton,
    disableSkipButton,
  } = useWizard();

  const totalSteps = useMemo(
    () => stepsConfig.reduce((acc, area) => acc + area.steps.length, 0),
    [stepsConfig],
  );

  const [activeStep, setActiveStep] = useState(0);

  // Find active macro/micro steps
  const { activeMacroStep, activeMicroStep } = useMemo(() => {
    let stepsSoFar = 0;
    for (let macroIdx = 0; macroIdx < stepsConfig.length; macroIdx++) {
      const areaSteps = stepsConfig[macroIdx].steps.length;
      if (activeStep < stepsSoFar + areaSteps) {
        return {
          activeMacroStep: macroIdx,
          activeMicroStep: activeStep - stepsSoFar,
        };
      }
      stepsSoFar += areaSteps;
    }

    // fallback (end of wizard)
    const lastArea = stepsConfig[stepsConfig.length - 1];
    const lastMicroStep = lastArea && lastArea.steps.length > 0 ? lastArea.steps.length - 1 : 0;
    return {
      activeMacroStep: stepsConfig.length > 0 ? stepsConfig.length - 1 : 0,
      activeMicroStep: lastMicroStep,
    };
  }, [activeStep, stepsConfig]);

  const goTo = useCallback(
    (step: number) => {
      setActiveStep(Math.max(0, Math.min(step, totalSteps - 1)));
    },
    [totalSteps],
  );

  const onNext = useCallback(
    (goToStep: number | undefined = undefined) => {
      if (goToStep !== undefined) {
        goTo(goToStep);
        return;
      }
      setActiveStep(prev => Math.min(prev + 1, totalSteps - 1));
    },
    [goTo, totalSteps],
  );

  const onPrevious = useCallback(
    (goToStep: number | undefined = undefined) => {
      if (goToStep !== undefined) {
        goTo(goToStep);
        return;
      }
      setActiveStep(prev => Math.max(prev - 1, 0));
    },
    [goTo],
  );

  const activeMacroStepData = useMemo(
    () => stepsConfig[activeMacroStep],
    [activeMacroStep, stepsConfig],
  );
  const activeMicroStepData = useMemo(
    () => activeMacroStepData.steps[activeMicroStep],
    [activeMacroStepData, activeMicroStep],
  );

  const handleNext = useCallback(() => {
    wizardHandleNext((goToStep: number | undefined = undefined) => {
      activeMicroStepData?.onNext?.();
      onNext(goToStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [activeMicroStepData, onNext, wizardHandleNext]);

  const handlePrevious = useCallback(() => {
    wizardHandlePrevious((goToStep: number | undefined = undefined) => {
      activeMicroStepData?.onPrevious?.();
      onPrevious(goToStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [activeMicroStepData, onPrevious, wizardHandlePrevious]);

  const handleSkip = useCallback(() => {
    wizardHandleSkip((goToStep: number | undefined = undefined) => {
      activeMicroStepData?.onSkip?.();
      onNext(goToStep); // Skip to the next step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [activeMicroStepData, onNext, wizardHandleSkip]);

  return {
    activeStep,
    activeMacroStep,
    activeMicroStep,
    activeMacroStepData,
    activeMicroStepData,
    totalSteps,
    isLoadingNextButton,
    disableNextButton,
    disablePreviousButton,
    disableSkipButton,
    onNext,
    onPrevious,
    goTo,
    handleNext,
    handlePrevious,
    handleSkip,
  };
}
